/**
 * Upgrade Command
 * Update jai1-client to the latest version
 * 
 * Renamed from 'self-update' to 'upgrade' for clarity
 * - 'update' = update installed components
 * - 'upgrade' = upgrade the CLI tool itself
 */
import { Command } from 'commander';
import { confirm } from '@inquirer/prompts';
import { execSync } from 'child_process';
import { ConfigService } from '../services/config.service.js';
import { ValidationError } from '../errors/index.js';
import { trackAction } from '../services/tracking.service.js';
import packageJson from '../../package.json' with { type: 'json' };
import { disableUpdateCheck } from '../services/version-check.service.js';
import { getCliName } from '../utils/cli-name.js';

// Colors for terminal output
const colors = {
    yellow: '\x1b[33m',
    green: '\x1b[32m',
    cyan: '\x1b[36m',
    red: '\x1b[31m',
    reset: '\x1b[0m',
    bold: '\x1b[1m',
};

/**
 * Create upgrade command
 */
export function createUpgradeCommand(): Command {
    return new Command('upgrade')
        .description('Upgrade CLI client to the latest version')
        .option('--check', 'Only check for updates without installing')
        .option('-y, --force', 'Upgrade without confirmation prompt (skip if already latest)')
        .action(async (options: { check?: boolean; force?: boolean }) => {
            await handleUpgrade(options);
        });
}

async function handleUpgrade(options: { check?: boolean; force?: boolean }) {
    const configService = new ConfigService();
    const config = await configService.load();

    if (!config) {
        throw new ValidationError(`Not initialized. Run "${getCliName()} auth" first.`);
    }

    try {
        // Fetch latest version from API
        console.log(`${colors.cyan}🔍 Checking for updates...${colors.reset}`);

        const response = await fetch(`${config.apiUrl}/api/versions/client`, {
            headers: {
                'JAI1-Access-Key': config.accessKey,
            },
            signal: AbortSignal.timeout(10000), // 10 second timeout
        });

        if (!response.ok) {
            throw new Error(`Failed to check for updates: ${response.statusText}`);
        }

        const data = await response.json() as {
            component: string;
            version: string;
            releasedAt: string;
            releaseNotes?: string;
            minClientVersion?: string;
        };
        const latestVersion = data.version;
        const currentVersion = packageJson.version;

        console.log(`\n${colors.bold}Current version:${colors.reset} ${currentVersion}`);
        console.log(`${colors.bold}Latest version:${colors.reset}  ${latestVersion}\n`);

        if (!isNewerVersion(latestVersion, currentVersion)) {
            if (options.force) {
                console.log(`${colors.green}✅ Already on the latest version (${currentVersion}). No upgrade needed.${colors.reset}\n`);
            } else {
                console.log(`${colors.green}✅ You're already on the latest version!${colors.reset}\n`);
            }
            return;
        }

        // Show update available
        console.log(`${colors.yellow}╭──────────────────────────────────────────────────────────╮${colors.reset}`);
        console.log(`${colors.yellow}│${colors.reset}  ${colors.bold}⬆️  Update available!${colors.reset} ${currentVersion} → ${colors.cyan}${latestVersion}${colors.reset}                  ${colors.yellow}│${colors.reset}`);
        console.log(`${colors.yellow}╰──────────────────────────────────────────────────────────╯${colors.reset}\n`);

        // If --check only, stop here
        if (options.check) {
            console.log(`${colors.cyan}Run "${getCliName()} upgrade" to install the latest version.${colors.reset}\n`);
            return;
        }

        // Ask for confirmation unless --force
        if (!options.force) {
            const shouldUpdate = await confirm({
                message: 'Update to the latest version now?',
                default: true
            });

            if (!shouldUpdate) {
                console.log(`${colors.yellow}⏸️  Upgrade cancelled.${colors.reset}\n`);
                return;
            }
        }

        // Perform update
        console.log(`\n${colors.cyan}📥 Installing latest version...${colors.reset}\n`);

        try {
            // Detect package manager (npm, pnpm, yarn, bun)
            const packageManager = detectPackageManager();
            const installCommand = getInstallCommand(packageManager);

            console.log(`${colors.cyan}Using ${packageManager}...${colors.reset}`);

            // Execute installation
            execSync(installCommand, {
                stdio: 'inherit',
                env: { ...process.env, FORCE_COLOR: '1' }
            });

            console.log(`\n${colors.green}✅ Successfully upgraded to version ${latestVersion}!${colors.reset}\n`);

            // Track successful update
            trackAction('upgrade', {
                from_version: currentVersion,
                to_version: latestVersion,
                package_manager: packageManager
            });

            // Disable update check for this session since we just upgraded
            disableUpdateCheck();

        } catch (error) {
            console.error(`\n${colors.red}❌ Upgrade failed!${colors.reset}`);
            console.error(`${colors.red}Error: ${error instanceof Error ? error.message : 'Unknown error'}${colors.reset}\n`);
            console.error(`${colors.yellow}💡 You can try manually upgrading with:${colors.reset}`);

            // Show manual upgrade command for detected package manager
            const manualCommands = {
                npm: `npm install -g @jvittechs/j@latest`,
                pnpm: `pnpm add -g @jvittechs/j@latest`,
                yarn: `yarn global add @jvittechs/j@latest`,
                bun: `bun add -g @jvittechs/j@latest`
            };

            console.error(`   ${colors.cyan}${manualCommands[packageManager]}${colors.reset}\n`);
            throw error;
        }

    } catch (error) {
        if (error instanceof Error && error.message.includes('Failed to check')) {
            throw new ValidationError('Cannot connect to update server. Please check your internet connection.');
        }
        throw error;
    }
}

/**
 * Compare versions to check if remote is newer than local
 */
function isNewerVersion(remote: string, local: string): boolean {
    const remoteParts = remote.split('.').map(Number);
    const localParts = local.split('.').map(Number);

    for (let i = 0; i < 3; i++) {
        const r = remoteParts[i] || 0;
        const l = localParts[i] || 0;

        if (r > l) return true;
        if (r < l) return false;
    }

    return false;
}

/**
 * Detect which package manager was used to install jai1
 * Priority:
 * 1. Check how jai1 was installed (via global bin path) - MOST RELIABLE
 * 2. Check npm_config_user_agent environment variable
 * 3. Default to npm (safest fallback)
 * 
 * Supports both Windows and Unix paths
 */
function detectPackageManager(): 'npm' | 'pnpm' | 'yarn' | 'bun' {
    // 1. Try to detect from actual jai1 installation path (MOST RELIABLE)
    try {
        const jai1Path = execSync('which j || which jai1 || where j || where jai1', {
            encoding: 'utf-8',
            stdio: ['pipe', 'pipe', 'ignore']
        }).trim();

        if (jai1Path) {
            // Normalize path separators for consistent matching (Windows uses \, Unix uses /)
            const normalizedPath = jai1Path.toLowerCase().replace(/\\/g, '/');

            // pnpm global paths:
            // - Unix: ~/.local/share/pnpm or ~/Library/pnpm
            // - Windows: C:/Users/username/AppData/Local/pnpm
            if (normalizedPath.includes('/pnpm/')) return 'pnpm';

            // yarn global paths:
            // - Unix: ~/.yarn or ~/Library/Yarn
            // - Windows: C:/Users/username/AppData/Local/Yarn
            if (normalizedPath.includes('/.yarn/') || normalizedPath.includes('/yarn/')) return 'yarn';

            // bun global paths:
            // - Unix: ~/.bun
            // - Windows: C:/Users/username/.bun
            if (normalizedPath.includes('/.bun/')) return 'bun';

            // npm global paths:
            // Unix:
            // - nvm: ~/.nvm/versions/node/vX.X.X/bin/
            // - system: /usr/local/bin/ or /usr/bin/
            // - npm prefix: ~/.npm-global/bin/ or similar
            // - node_modules: anywhere with /node_modules/.bin/
            // Windows:
            // - AppData: C:/Users/username/AppData/Roaming/npm/
            // - Program Files: C:/Program Files/nodejs/
            // - node_modules: anywhere with /node_modules/
            if (normalizedPath.includes('/.nvm/') ||
                normalizedPath.includes('/node_modules/') ||
                normalizedPath.includes('/lib/node_modules/') ||
                normalizedPath.includes('/npm-global/') ||
                normalizedPath.includes('/usr/local/bin/') ||
                normalizedPath.includes('/usr/bin/') ||
                normalizedPath.includes('/appdata/roaming/npm/') ||
                normalizedPath.includes('/program files/nodejs/')) {
                return 'npm';
            }
        }
    } catch {
        // Ignore errors, continue with other detection methods
    }

    // 2. Check user agent for package manager (when running via npm/pnpm/yarn scripts)
    const userAgent = process.env.npm_config_user_agent || '';

    if (userAgent.includes('pnpm')) return 'pnpm';
    if (userAgent.includes('yarn')) return 'yarn';
    if (userAgent.includes('bun')) return 'bun';
    if (userAgent.includes('npm')) return 'npm';

    // 3. Default to npm (safest and most common)
    // DO NOT check for available package managers here - it causes false positives!
    // If user has pnpm installed but used npm to install jai1, we should use npm.
    return 'npm';
}

/**
 * Get install command for package manager
 */
function getInstallCommand(packageManager: 'npm' | 'pnpm' | 'yarn' | 'bun'): string {
    const packageName = '@jvittechs/j@latest';

    switch (packageManager) {
        case 'pnpm':
            return `pnpm add -g ${packageName}`;
        case 'yarn':
            return `yarn global add ${packageName}`;
        case 'bun':
            return `bun add -g ${packageName}`;
        case 'npm':
        default:
            return `npm install -g ${packageName}`;
    }
}
